import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import '../styles/design-system.css';
import './AnalyticsPage.css';

const IP = { viewBox:'0 0 24 24', fill:'none', stroke:'currentColor', strokeWidth:2, strokeLinecap:'round', strokeLinejoin:'round' };
const Icon = {
  Click:   (p) => (<svg width="14" height="14" {...IP} strokeWidth="2.5" {...p}><path d="m9 9 5 12 1.8-5.2L21 14Z"/></svg>),
  Bounce:  (p) => (<svg width="14" height="14" {...IP} strokeWidth="2.5" {...p}><path d="m17 7-5 5-5-5"/><path d="m17 13-5 5-5-5"/></svg>),
  Users:   (p) => (<svg width="14" height="14" {...IP} strokeWidth="2.5" {...p}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>),
  Clock:   (p) => (<svg width="14" height="14" {...IP} strokeWidth="2.5" {...p}><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>),
};

const RANGES = ['24h','7d','30d','90d','All'];

// CSS-based flag rendering for donut/analytics
function FlagEl({ code, size = 18 }) {
  const flagClass = code && code !== 'un' ? `flag ${code}` : 'flag';
  return (
    <span className={flagClass} style={{ width: size, height: Math.round(size*0.7), display:'inline-flex', flexShrink:0, border:'1px solid rgba(13,13,15,0.08)', borderRadius:2 }}>
      {code === 'nl' && <><span/><span/><span/></>}
    </span>
  );
}

// Animated bar fill that triggers on mount
function AnimBar({ target, variant }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = 'scaleX(0)';
    const t = setTimeout(() => {
      el.style.transform = `scaleX(${target})`;
    }, 100);
    return () => clearTimeout(t);
  }, [target]);
  return (
    <div className="bar-track">
      <div ref={ref} className={`bar-fill ${variant || ''}`} style={{ '--target': target, transition: 'transform 1.2s ease-out' }} />
    </div>
  );
}

// Animated funnel fill
function FunnelFill({ target }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = 'scaleX(0)';
    const t = setTimeout(() => { el.style.transform = `scaleX(${target})`; }, 150);
    return () => clearTimeout(t);
  }, [target]);
  return <div ref={ref} className="funnel-fill" />;
}

// Heatmap
function Heatmap({ data }) {
  const days = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const hours = Array.from({length:24},(_,i)=>i);
  return (
    <div className="heatmap">
      {days.map(day => (
        <div key={day} className="heatmap-row">
          <div className="heatmap-label">{day}</div>
          {hours.map(h => {
            const val = data?.[day]?.[h] ?? 0;
            const normalized = val / (data?._max || 1);
            const isWeekend = day === 'Sat' || day === 'Sun';
            const color = normalized > 0.6 ? '#ff5436' : normalized > 0.3 ? '#e8b94a' : '#8b9b3f';
            const opacity = 0.1 + normalized * 0.9;
            return (
              <div
                key={h}
                className="heatmap-cell"
                style={{ background: color, opacity }}
                title={`${day} ${h}:00 — ${Math.floor(normalized*1000)} clicks`}
              />
            );
          })}
        </div>
      ))}
      <div className="heatmap-cols">
        <div className="heatmap-label" style={{visibility:'hidden'}}>xxx</div>
        {hours.map(h => (
          <div key={h} className="heatmap-col-label">{h % 6 === 0 ? h : ''}</div>
        ))}
      </div>
    </div>
  );
}

// Generate mock heatmap data
function mockHeatmap() {
  const days = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const data = { _max: 0 };
  days.forEach(day => {
    data[day] = {};
    const isWeekend = day === 'Sat' || day === 'Sun';
    for (let h = 0; h < 24; h++) {
      const businessHour = h >= 9 && h <= 18;
      let v = Math.random() * 300;
      if (businessHour && !isWeekend) v += 400 + Math.random() * 300;
      if (h >= 12 && h <= 14 && !isWeekend) v += 200;
      data[day][h] = Math.round(v);
      if (data[day][h] > data._max) data._max = data[day][h];
    }
  });
  return data;
}

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const [range, setRange] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const heatmapData = useRef(mockHeatmap());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/analytics/workspace', { params: { date_range: range } });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Derive analytics data from API or use defaults for display
  const totalClicks  = data?.total_clicks || 0;
  const uniqueVisitors = data?.unique_visitors || 0;
  const countries = data?.countries || [];

  // Device distribution (mock if not available from API)
  const deviceData = [
    { label: 'Mobile',  pct: 55, num: Math.round(totalClicks * 0.55), color: '#ff5436' },
    { label: 'Desktop', pct: 33, num: Math.round(totalClicks * 0.33), color: '#8b9b3f' },
    { label: 'Tablet',  pct: 12, num: Math.round(totalClicks * 0.12), color: '#e8b94a' },
  ];
  // circumference ≈ 251.3 for r=40
  const C = 251.3;
  let offset = 0;
  const segments = deviceData.map(d => {
    const len = C * d.pct / 100;
    const seg = { ...d, dasharray: `${len} ${C - len}`, dashoffset: -offset };
    offset += len;
    return seg;
  });

  const browsers = [
    { label: 'Chrome',  pct: 62, target: 1,    variant: '' },
    { label: 'Safari',  pct: 21, target: 0.34, variant: 'olive' },
    { label: 'Firefox', pct: 10, target: 0.16, variant: 'dark' },
    { label: 'Edge',    pct: 5,  target: 0.08, variant: 'mustard' },
    { label: 'Other',   pct: 2,  target: 0.03, variant: 'dark' },
  ];

  const referrers = [
    { label: 'linkedin.com',  val: '38.2k', target: 1,    variant: '' },
    { label: 'twitter.com',   val: '27.1k', target: 0.71, variant: 'olive' },
    { label: 'google.com',    val: '19.5k', target: 0.51, variant: 'mustard' },
    { label: 'youtube.com',   val: '13.7k', target: 0.36, variant: 'dark' },
    { label: '(direct)',      val: '8.2k',  target: 0.21, variant: 'dark' },
  ];

  const funnel = [
    { num: totalClicks > 0 ? (totalClicks/1000).toFixed(0)+'k' : '248k', text: 'Total clicks',        pct: '100%',  target: 1 },
    { num: totalClicks > 0 ? Math.round(totalClicks*0.78/1000)+'k' : '194k', text: 'Reached destination', pct: '78.0%', target: 0.78 },
    { num: totalClicks > 0 ? Math.round(totalClicks*0.42/1000)+'k' : '104k', text: 'Engaged (≥10s)',      pct: '41.9%', target: 0.42 },
    { num: totalClicks > 0 ? Math.round(totalClicks*0.084/1000)+'k' : '21k', text: 'Converted',           pct: '8.4%',  target: 0.084 },
  ];

  const changes = data?.changes || {};
  const fmtPct = (v) => v !== undefined && v !== null ? `${v > 0 ? '↑' : '↓'} ${Math.abs(v).toFixed(1)}%` : null;

  return (
    <div className="ds-page" data-testid="analytics-page">
      {/* PAGE HEADER */}
      <div className="page-header">
        <div>
          <div className="page-label olive">Analytics · deep dive</div>
          <h1 className="page-title">Where your clicks <em>come from</em>.</h1>
          <p className="page-sub">Devices, browsers, referrers, conversions — the full picture.</p>
        </div>
        <div className="range-tabs">
          {RANGES.map(r => (
            <button key={r} className={`range-tab${range===r?' active':''}`} onClick={()=>setRange(r)}>{r}</button>
          ))}
        </div>
      </div>

      {/* KPI ROW */}
      <div className="kpi-row">
        <div className="kpi-card primary">
          <div className="kpi-head">
            <div className="kpi-label">Total clicks · {range}</div>
            <div className="kpi-icon dark"><Icon.Click /></div>
          </div>
          <div className="kpi-value">{totalClicks > 0 ? totalClicks.toLocaleString() : (loading ? '—' : '248,471')}</div>
          <div className="kpi-foot">
            {changes.total_clicks != null && <span className={`kpi-delta ${changes.total_clicks>=0?'up':'down'}`}>{fmtPct(changes.total_clicks)}</span>}
            <span className="kpi-meta">vs previous {range}</span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-head">
            <div className="kpi-label">Bounce rate</div>
            <div className="kpi-icon coral"><Icon.Bounce /></div>
          </div>
          <div className="kpi-value">23.8%</div>
          <div className="kpi-foot"><span className="kpi-delta down">↓ 1.4pp</span></div>
        </div>
        <div className="kpi-card">
          <div className="kpi-head">
            <div className="kpi-label">New visitors</div>
            <div className="kpi-icon olive"><Icon.Users /></div>
          </div>
          <div className="kpi-value">{uniqueVisitors > 0 ? Math.round(uniqueVisitors/totalClicks*100)+'%' : '62.3%'}</div>
          <div className="kpi-foot"><span className="kpi-delta up">↑ 4.2pp</span></div>
        </div>
        <div className="kpi-card">
          <div className="kpi-head">
            <div className="kpi-label">Sessions</div>
            <div className="kpi-icon mustard"><Icon.Clock /></div>
          </div>
          <div className="kpi-value">{uniqueVisitors > 0 ? Math.round(uniqueVisitors/1000)+'k' : '132k'}</div>
          <div className="kpi-foot"><span className="kpi-delta up">↑ 8.7%</span></div>
        </div>
      </div>

      {/* DEVICES + BROWSERS */}
      <div className="analytics-grid">
        <div className="panel">
          <div className="panel-head">
            <div>
              <div className="panel-title">Devices</div>
              <div className="panel-sub">Distribution across device types</div>
            </div>
          </div>
          <div className="donut-wrap">
            <svg className="donut-svg" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="40" fill="none" stroke="var(--bg-warm)" strokeWidth="22"/>
              {segments.map(seg => (
                <circle key={seg.label} cx="60" cy="60" r="40" fill="none"
                  stroke={seg.color} strokeWidth="22"
                  strokeDasharray={seg.dasharray}
                  strokeDashoffset={seg.dashoffset}
                  transform="rotate(-90 60 60)"
                />
              ))}
              <text x="60" y="58" textAnchor="middle" fontFamily="Instrument Serif" fontSize="22" fontStyle="italic" fill="var(--ink)">
                {totalClicks > 0 ? Math.round(totalClicks/1000)+'k' : '248k'}
              </text>
              <text x="60" y="72" textAnchor="middle" fontFamily="Geist Mono" fontSize="8" fill="var(--ink-faint)">CLICKS</text>
            </svg>
            <div className="donut-legend">
              {deviceData.map(d => (
                <div key={d.label} className="donut-row">
                  <div className="donut-label">
                    <span className="donut-swatch" style={{background:d.color}}/>
                    {d.label}
                  </div>
                  <div className="donut-meta">
                    <div className="donut-pct">{d.pct}%</div>
                    <div className="donut-num">{d.num.toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <div>
              <div className="panel-title">Top browsers</div>
              <div className="panel-sub">Where your traffic browses from</div>
            </div>
          </div>
          <div className="bars-panel">
            {browsers.map(b => (
              <div key={b.label} className="bar-row">
                <span className="bar-label">{b.label}</span>
                <AnimBar target={b.target} variant={b.variant} />
                <span className="bar-num">{b.pct}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* REFERRERS + FUNNEL */}
      <div className="analytics-grid">
        <div className="panel">
          <div className="panel-head">
            <div>
              <div className="panel-title">Top referrers</div>
              <div className="panel-sub">Where your clicks originate</div>
            </div>
          </div>
          <div className="bars-panel">
            {referrers.map(r => (
              <div key={r.label} className="bar-row">
                <span className="bar-label" style={{fontFamily:"'Geist Mono',monospace",fontSize:12}}>{r.label}</span>
                <AnimBar target={r.target} variant={r.variant} />
                <span className="bar-num">{r.val}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <div>
              <div className="panel-title">Conversion funnel</div>
              <div className="panel-sub">From click to conversion</div>
            </div>
          </div>
          <div className="funnel">
            {funnel.map((step, i) => (
              <div key={i} className="funnel-step">
                <FunnelFill target={step.target} />
                <div className="funnel-info">
                  <div className="funnel-num">{step.num}</div>
                  <div className="funnel-text">{step.text}</div>
                </div>
                <div className="funnel-pct">{step.pct}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* HEATMAP */}
      <div className="panel" style={{marginBottom:32}}>
        <div className="panel-head">
          <div>
            <div className="panel-title">Click heatmap</div>
            <div className="panel-sub">When your audience is clicking · last 7 days · UTC</div>
          </div>
        </div>
        <Heatmap data={heatmapData.current} />
      </div>
    </div>
  );
}
